import { LoadingSpinner, Text } from "@microbeta/ui";
import {
  HamburgerMenu,
  MicrobetaLogo,
  Navigation,
  Sidebar,
} from "@shared/components";
import { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, ScrollRestoration, useLocation } from "react-router-dom";
import { useAuthContext } from "../../features/auth/AuthContext";
import { useAnalytics } from "../../shared/hooks";
import { paths } from "../../shared/utils/routing";
import styles from "./root-layout.module.scss";

export const RootLayout = () => {
  const { t } = useTranslation();
  const { authState } = useAuthContext();
  const location = useLocation();
  const { trackPageView } = useAnalytics();

  // Check if we're on the homepage
  const isHomePage = location.pathname === paths.index;

  // Track page navigation events
  useEffect(() => {
    if (authState.userId) {
      trackPageView(isHomePage ? "home_page" : "generic_page", {
        user_id: authState.userId,
        page_path: location.pathname,
        page_title: document.title,
        is_homepage: isHomePage,
        referrer: document.referrer,
      });
    }
  }, [location.pathname, authState.userId, isHomePage, trackPageView]);

  return (
    <div className={styles.root}>
      <ScrollRestoration />
      {authState && (
        <div
          className={[
            styles.header,
            isHomePage ? styles.headerGradient : "",
          ].join(" ")}
        >
          <div className={styles.leftSection}>
            <HamburgerMenu />
          </div>
          <Link to={paths.home} className={styles.logoContainer}>
            <MicrobetaLogo />
          </Link>
          <Navigation />
        </div>
      )}

      <div className={styles.contentWrapper}>
        {authState && <Sidebar />}
        <div className={styles.outletContainer}>
          <main className={styles.mainContent}>
            <Suspense fallback={<LoadingSpinner useRandomPhrase />}>
              <Outlet />
            </Suspense>
          </main>
          <footer className={styles.footer}>
            <div className={styles.footerContent}>
              <Text variant="small" className={styles.footerText}>
                {t("rootLayout.footer.copyright", {
                  year: new Date().getFullYear(),
                })}
              </Text>
              <nav className={styles.footerNav}>
                <Link to={paths.legal.index} className={styles.footerLink}>
                  {t("rootLayout.footer.termsAndPrivacy")}
                </Link>
              </nav>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};
